import { Head } from '@components/common/head';
import { SectionSeparator } from '@styles/layout';
import React, { useEffect } from 'react';
import { PageProps } from '@model/pages';
import { CMSDestination, CMSTravelGuides, TravelGuide, TripTypes } from '@model/contentful';
import styled from '@emotion/styled';
import { breakpoints, mq } from '@styles/breakpoints';
import { NavBarContainer } from '@components/navigation';
import { Place } from '@model/iceberg/geography';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Node } from '@contentful/rich-text-types';
import { ContentLayout } from '@components/common/layout';
import { TravelGuideSideBar } from '@components/guides';
import { contentfulBackgroundImage } from '@styles/mixins';
import { useDispatch, useSelector } from 'react-redux';
import { GuideBreadcrumb } from '@components/breadcrumbs';
import { Error404 } from '@components/common/hero/error/Error404';
import { Footer } from '@components/footer';
import { Page } from '@model/common';
import { useI18NextContext } from '@components/hooks';
import { setTripTypeFilter } from '@state/navigation';
import { getImageUrl } from '@util/images';
import { getCMSDestination } from '@state/cms';
import { CMSComponents } from '@components/cms';
import SearchTabsBar from '@components/search/SearchTabsBar';
import { ThemeProps } from '@theme/base';
import { ERROR_CODES } from '@model/common/error-codes';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TravelGuidePageLayoutProps extends PageProps {
  cmsTravelGuide: TravelGuide;
  cmsTravelGuides: CMSTravelGuides;
  place: Place;
  path: string;
  tripType?: TripTypes;
  cmsConfig: any;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const HeroImage: any = styled.div(({ url }: any) => ({
  height: 220,
  backgroundImage: contentfulBackgroundImage(url, breakpoints.small),
  backgroundSize: 'cover',
  backgroundPosition: 'center',

  [mq.small]: {
    backgroundImage: contentfulBackgroundImage(url, breakpoints.medium),
    height: 330
  },

  [mq.medium]: {
    backgroundImage: contentfulBackgroundImage(url, breakpoints.large),
    height: 467
  },

  [mq.large]: {
    backgroundImage: contentfulBackgroundImage(url, breakpoints.xLarge)
  }
}));

const ContentImage = styled.img(({ theme }: ThemeProps) => ({
  width: '100%',
  margin: `${theme.custom.spacing.medium}px 0`
}));

export const TravelGuidePageLayout = ({
  cmsTravelGuide,
  path,
  cmsTravelGuides,
  tripType,
  cmsConfig,
  promoBannerTripType
}: TravelGuidePageLayoutProps) => {
  const t: any = useI18NextContext();
  const destination: CMSDestination | undefined = useSelector(getCMSDestination(path));
  const destinationPath: string = destination?.destinationPath?.fields?.path || '';
  const destinationTitle: string = destination?.title || '';
  if (!cmsTravelGuide) return <Error404 code={ERROR_CODES.NO_TRAVEL_GUIDE} />;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setTripTypeFilter(tripType || TripTypes.ALL));
  }, []);

  const richTextRendererOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
        const imageSrc = node.data.target.fields.file.url;
        return (
          <ContentImage
            src={`${getImageUrl(imageSrc, breakpoints.small)}`}
            srcSet={`${getImageUrl(imageSrc, breakpoints.small)} ${breakpoints.small}w, ${getImageUrl(
              imageSrc,
              breakpoints.medium
            )} ${breakpoints.medium}w`}
          />
        );
      }
    }
  };
  const article: string = cmsTravelGuide.title;
  const metaTitle: string = cmsTravelGuide.metaTitle
    ? `${cmsTravelGuide.metaTitle} | ${t('common__brand_display')}`
    : t('seo__travel-guide--title', { article });
  const metaDescription: string =
    cmsTravelGuide.metaDescription ||
    t('seo__travel-guide--description', {
      article,
      destination: destinationTitle
    });
  return (
    <>
      <Head metaTitle={metaTitle} metaDescription={metaDescription} />
      <NavBarContainer promoBannerTripType={promoBannerTripType} renderSearch={<SearchTabsBar condensed />} />
      <HeroImage url={cmsTravelGuide.heroImage.fields.file.url} />
      <GuideBreadcrumb
        destination={{
          path: destinationPath,
          display: destinationTitle
        }}
        guide={cmsTravelGuide.path}
        guideDisplay={cmsTravelGuide.title}
        tripType={tripType}
        guidePage={Page.TRAVEL_GUIDES}
      />
      <SectionSeparator />
      <ContentLayout
        maxWidth
        main={<>{documentToReactComponents(cmsTravelGuide.content, richTextRendererOptions)}</>}
        sidebar={
          <>
            <SectionSeparator />
            <TravelGuideSideBar
              cmsTravelGuides={cmsTravelGuides || []}
              currentGuidePath={cmsTravelGuide.path}
              path={path}
              tripType={tripType}
            />
          </>
        }
      />
      <SectionSeparator />
      <CMSComponents components={cmsConfig.components} {...{ destination, tripType }} />
      <SectionSeparator />
      <Footer />
    </>
  );
};
